import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { FFIBrandedV1Report } from "src/pages/reports/ffi/v1/types";
import { isNotNil } from "src/utils/type_guards";
import { DomainsCircle } from "../../graphics/domains_circle";

import { meta } from "./meta";

/*
 * Types
 */

export type PracticalProps = {
    readonly report: FFIBrandedV1Report;
};

/*
 * Main
 */

export const Practical = (props: PracticalProps): React.ReactElement => {
    const { report } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="practical" inline />
                <PageTitle style={{ color: "#99b84e" }}>Practical</PageTitle>
                <Body>
                    Faith is lived out in the hands. God writes His law upon our hearts and causes us to walk according
                    to His statutes (Jeremiah 31:31-34). Christian schools put policies in place and engage in practices
                    that foster spiritual disciplines in all members of the school community.
                </Body>
                <Body>
                    Such practices may be implemented at the school level, such as a policy that promotes
                    Sabbath-keeping or the ways in which the board helps guard the mission. They may take place in the
                    classroom, as when a school provides practical skills for evaluating the world. They may also exist
                    in the family, as when parents read the Bible with their children or protect them from potentially
                    harmful content. FFI data analysis identified constructs related to the Practical domain for all
                    seven constituent groups (students; parents; alumni; teachers; leaders/administrators; and support
                    staff), below.
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FFI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(report.value.results.raw.practical),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle style={{ color: "#99b84e" }}>{m.title}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        backgroundColor="#99b94e"
                                        data={[
                                            {
                                                label: report.value.year,
                                                value: report.value.results.raw.practical[construct] * 100,
                                            },
                                            {
                                                label: "FFI Benchmark",
                                                value: report.value.results.benchmarks.practical[construct] * 100,
                                            },
                                        ]}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
